/*
 * cf-sku-stamp.js — Click Fencing — 2026-09-07
 *
 * A frameless glass order was sending the supplier a purchase order with every line
 * priced $0.00. This puts the SKU back on the line before the order note leaves the
 * browser.
 *
 * WHAT WAS WRONG, MEASURED
 * ------------------------
 * The order note is the picking list, and each part carries a tag the CRM matches on:
 *
 *     • Flat Top Panel 2400mm (x2) — $228.98  [sku:49312|vid:42901289271390]   <- good
 *     • 1550mm Glass Panels (x2)   — $270.54  [vid:42018445295710]             <- no sku
 *
 * `resolveProducts()` in the route-paid-orders edge function looks the part up by
 * `products.supplier_sku` and nothing else, so a vid-only line resolves to no product,
 * gets `supplier_cost` 0, and the PO line costs $0.00. Captured live on 2026-09-07 by
 * intercepting /cart/update.js: a flat top quote sent 3 sku tags and 0 vid-only tags,
 * an identical glass quote sent 0 sku tags and 3 vid-only. Four of the five real glass
 * orders in the store (#1018, #1020, #1021, #1022) have no sku tag on any line;
 * #1019 is the only one that does. route-paid-orders' own header already recorded the
 * consequence without knowing the cause: "#1018 and #1020 (0 sku tags) -> $0".
 *
 * WHY FIX IT HERE RATHER THAN IN THE CALCULATOR
 * ---------------------------------------------
 * The tag is written deep inside quote-review.js's checkout handler, which is minified
 * and not reachable from outside. But every note goes out through exactly one request —
 * POST /cart/update.js with {note, attributes} — which IS reachable. Correcting it at
 * that single choke point covers all four calculators and every route to checkout,
 * including any future one, and it cannot be outrun by whatever async price data the
 * page was waiting on. It is the same wrap-window.fetch pattern cf-calc-fixes.js
 * already uses in production.
 *
 * WHERE THE SKU COMES FROM
 * ------------------------
 * `window.CF_SKU_MAP` is on the page already: 399 entries keyed by SKU, each
 * `{id: <shopify variant id>, cents: <price>}`. Inverted it gives variant id -> SKU,
 * with no collisions (399 in, 399 out — checked). Verified against the three variant
 * ids in a real glass note: 42018445295710 -> 48203, 42018445361246 -> 48204,
 * 41519272853598 -> 49968.
 *
 * ⚠️ RECOVERING THE SKU IS NOT THE SAME AS THE PART BEING PRICED. 48203 and 48204 are
 * in `products` at $80.39 and $82.98 supplier cost; **49968, the core drill spigot, is
 * not in the products table at all**, so that line still costs $0 after this fix. That
 * is a catalogue gap, not a code one, and it needs a row adding — see the vault.
 *
 * SAFETY
 * ------
 * Only ever ADDS a sku to a tag that has none. `[sku:x|vid:y]` cannot match the pattern,
 * so a correctly stamped note is passed through byte for byte. Any parse failure, any
 * missing map, any thrown error and the ORIGINAL request goes out untouched — the worst
 * case is exactly the behaviour we have today.
 */
(function () {
  "use strict";

  if (window.__cfSkuStamp) return;
  window.__cfSkuStamp = true;

  var TAG = /\[vid:(\d+)\]/g;

  // Built fresh on each request. CF_SKU_MAP is populated asynchronously by
  // cf-sku-prices.js, and building this once at load time would bake in whatever was
  // ready then — which is the very race that produced the vid-only notes.
  function inverseMap() {
    var m = window.CF_SKU_MAP;
    if (!m || typeof m !== "object") return null;
    var inv = {}, n = 0;
    for (var sku in m) {
      if (!Object.prototype.hasOwnProperty.call(m, sku)) continue;
      var id = m[sku] && m[sku].id;
      if (id == null) continue;
      inv[String(id)] = String(sku);
      n++;
    }
    return n ? inv : null;
  }

  function stampNote(note) {
    if (typeof note !== "string" || note.indexOf("[vid:") === -1) return null;
    var inv = inverseMap();
    if (!inv) return null;
    var added = 0, missed = [];
    var out = note.replace(TAG, function (whole, vid) {
      var sku = inv[String(vid)];
      if (!sku) { if (missed.length < 8) missed.push(vid); return whole; }
      added++;
      return "[sku:" + sku + "|vid:" + vid + "]";
    });
    if (!added) {
      if (missed.length) {
        try { console.warn("[cf-sku-stamp] no SKU known for variant ids:", missed.join(", ")); } catch (e) {}
      }
      return null;
    }
    try {
      console.log("[cf-sku-stamp] recovered " + added + " SKU tag" + (added === 1 ? "" : "s") +
        " on the order note" + (missed.length ? " (" + missed.length + " still unknown)" : ""));
    } catch (e2) {}
    return out;
  }

  var origFetch = window.fetch;
  if (typeof origFetch !== "function") return;

  window.fetch = function (input, init) {
    try {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      // Only the one request that carries the note. Everything else is untouched.
      if (/\/cart\/update(\.js)?(\?|$)/.test(String(url)) && init && typeof init.body === "string") {
        var payload = JSON.parse(init.body);          // throws -> original request
        var fixed = payload && stampNote(payload.note);
        if (fixed) {
          payload.note = fixed;
          var next = {};
          for (var k in init) if (Object.prototype.hasOwnProperty.call(init, k)) next[k] = init[k];
          next.body = JSON.stringify(payload);
          return origFetch.call(this, input, next);
        }
      }
    } catch (e) {
      try { console.warn("[cf-sku-stamp] left the request alone:", e && e.message); } catch (e2) {}
    }
    return origFetch.apply(this, arguments);
  };

  try { console.log("[cf-sku-stamp] installed"); } catch (e) {}
})();
